// PK-11: pure orchestration for the 4-tier candle cache cascade. No React.
// Owns the EF-133 invariants (empty-result poison guard, dedup-by-time,
// strict-ascending sort) and the EF-132 forming-bar refetch guard. The hook
// useCandleStore wraps this; TradeChart and (PK-12) StrategyChart consume
// the hook so this is the single source of truth for fetch + cache + the
// IBKR cascade.

import {
  getCachedCandles,
  getCachedCandleMeta,
  setCachedCandles,
  mergeCachedCandles,
  isCacheFresh,
  barsReachLastSession,
  barsReachLastCompletedSession,
} from './candleCache'
import { toChartTimestamp } from './timezone'
import { parseOCC, isOCCSymbol } from './occParser'
import { supabase } from './supabase'
import { getMarketSession } from './marketSession'

// ── Module state ────────────────────────────────────────────────────────────

const _cache = new Map()
const CACHE_TTL = 5 * 60 * 1000
const IBKR_CANDLE_FETCH_TIMEOUT_MS = 95 * 1000
const LIVE_STAGING_ORIGIN = 'https://erevis-live-staging.bigworldcapitalgroup.com'
const PRODUCTION_ORIGIN = 'https://erevis.bigworldcapitalgroup.com'
const CANONICAL_BARS_FETCH_LIMIT = 50000
const CANONICAL_FIRST_PAINT_FETCH_LIMIT = 600
const CANONICAL_SCROLLBACK_CHUNK_DAYS = 10
const DURATION_COVERAGE_START_TOLERANCE_SECONDS = 4 * 86400
const MIN_CANONICAL_FIRST_PAINT_BARS_BY_IBKR_SIZE = new Map([
  ['1 min', 400],
  ['2 mins', 400],
  ['5 mins', 400],
  ['15 mins', 300],
  ['30 mins', 200],
  ['65 mins', 120],
  ['1 day', 120],
])
const CANONICAL_BAR_SIZE_BY_IBKR_SIZE = new Map([
  ['1 min', '1min'],
  ['2 mins', '2min'],
  ['5 mins', '5min'],
  ['15 mins', '15min'],
  ['30 mins', '30min'],
  ['65 mins', '65min'],
  ['1 day', '1day'],
  ['1 week', '1week'],
])

// EF-190: cache the Supabase session at module scope so every chart load
// stops paying the 100-500ms supabase.auth.getSession() roundtrip. 30s TTL
// is short enough that a token refresh during a Supabase auth event is
// picked up on the next call after expiry; we don't need to wire into
// onAuthStateChange for this.
let _cachedSession = null
let _cachedSessionAt = 0
const SESSION_CACHE_TTL = 30 * 1000

async function getCachedSession() {
  if (_cachedSession && Date.now() - _cachedSessionAt < SESSION_CACHE_TTL) {
    return _cachedSession
  }
  // If getSession() throws (rare — network blip), do NOT cache the failure.
  // Let the error propagate to the caller (fetchBars treats it as auth-fail
  // and surfaces via useCandleStore → status='error' → EF-133 retry).
  // The next call will retry getSession() instead of being stuck on a
  // poisoned cache.
  try {
    const { data: { session } } = await supabase.auth.getSession()
    _cachedSession = session
    _cachedSessionAt = Date.now()
    return session
  } catch (err) {
    _cachedSession = null
    _cachedSessionAt = 0
    throw err
  }
}

// Test-only: reset the module-level session cache between tests.
export function __resetSessionCacheForTests() {
  _cachedSession = null
  _cachedSessionAt = 0
}

// EF-132: module-level guard so concurrent ticks don't stack forming-bar
// fetches. Released 60s after the first fetch completes (success or error).
let _refetchingBar = null
const MARKET_DATA_SESSION_REGULAR = 'regular'
const MARKET_DATA_SESSION_ALL = 'all'

function normalizeMarketDataSession(session) {
  return session === MARKET_DATA_SESSION_ALL ? MARKET_DATA_SESSION_ALL : MARKET_DATA_SESSION_REGULAR
}

// ── Cache key — single source of truth ──────────────────────────────────────
// TradeChart, StrategyChart (PK-12), and any future consumer must use this.
// No inline key strings anywhere in the codebase.

export function buildCacheKey(symbol, barSize, optContract, session = MARKET_DATA_SESSION_REGULAR) {
  const sessionKey = normalizeMarketDataSession(session)
  return optContract
    ? `OPT:${optContract.underlying}:${optContract.expiry}:${optContract.right}:${optContract.strike}:${barSize}:${sessionKey}`
    : `${symbol}:${barSize}:${sessionKey}`
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function yyyymmddToEpochSeconds(value) {
  const ts = String(value)
  const y = +ts.slice(0, 4), m = +ts.slice(4, 6) - 1, d = +ts.slice(6, 8)
  if (isNaN(y) || isNaN(m) || isNaN(d)) return 0
  return Math.floor(Date.UTC(y, m, d) / 1000)
}

export function rawTimeToEpoch(rawTime) {
  if (typeof rawTime === 'number' && Number.isFinite(rawTime)) {
    if (Number.isInteger(rawTime) && rawTime >= 19000101 && rawTime <= 99991231) {
      return yyyymmddToEpochSeconds(rawTime)
    }
    return rawTime
  }
  const ts = String(rawTime)
  if (!ts || ts === '0') return 0
  return yyyymmddToEpochSeconds(ts)
}

function getRuntimeOrigin() {
  if (typeof window === 'undefined') return ''
  return window.location?.origin || ''
}

function readViteEnvFlag(name) {
  return import.meta.env?.[name] === '1' || import.meta.env?.[name] === 'true'
}

function readViteEnvSwitch(name) {
  const value = String(import.meta.env?.[name] ?? '').trim().toLowerCase()
  if (['1', 'true', 'yes', 'on'].includes(value)) return true
  if (['0', 'false', 'no', 'off'].includes(value)) return false
  return null
}

export function canonicalBarsReadEnabled({ origin = getRuntimeOrigin() } = {}) {
  if (readViteEnvFlag('VITE_MARKET_DATA_CANONICAL_BARS_ROLLBACK')) return false
  if (origin === LIVE_STAGING_ORIGIN) return true
  if (origin === PRODUCTION_ORIGIN) {
    return readViteEnvFlag('VITE_MARKET_DATA_PRODUCTION_CUTOVER_ENABLED')
  }
  return false
}

export function canonicalBarsStrictModeEnabled({ origin = getRuntimeOrigin() } = {}) {
  if (!canonicalBarsReadEnabled({ origin })) return false
  const strictConfigured = readViteEnvSwitch('VITE_MARKET_DATA_CANONICAL_BARS_STRICT')
  return strictConfigured === null ? true : strictConfigured
}

function canonicalBarSizeForIbkrSize(barSize) {
  return CANONICAL_BAR_SIZE_BY_IBKR_SIZE.get(barSize) || null
}

function canonicalFirstPaintMinBars(barSize) {
  return MIN_CANONICAL_FIRST_PAINT_BARS_BY_IBKR_SIZE.get(barSize) || null
}

function canonicalFirstPaintFetchLimit(barSize) {
  if (barSize === '1 day' || barSize === '1 week') return CANONICAL_BARS_FETCH_LIMIT
  return canonicalFirstPaintMinBars(barSize)
    ? CANONICAL_FIRST_PAINT_FETCH_LIMIT
    : CANONICAL_BARS_FETCH_LIMIT
}

function canonicalFetchBarSizeForTimeframe(timeframe) {
  if (timeframe === '65min') return '65 mins'
  const tf = IBKR_TF[timeframe] || IBKR_TF['daily']
  return tf.barSize
}

function canonicalBarSizeForTimeframe(timeframe) {
  return canonicalBarSizeForIbkrSize(canonicalFetchBarSizeForTimeframe(timeframe))
}

function includeCurrentCanonicalBar(barSize, session = MARKET_DATA_SESSION_REGULAR) {
  const marketSession = normalizeMarketDataSession(session)
  const currentSession = getMarketSession()
  return canonicalBarSizeForIbkrSize(barSize) !== null
    && barSize !== '1 day'
    && barSize !== '1 week'
    && (
      (marketSession === MARKET_DATA_SESSION_REGULAR && currentSession === 'regular')
      || (marketSession === MARKET_DATA_SESSION_ALL && ['pre', 'regular', 'post'].includes(currentSession))
    )
}

function durationToFromSeconds(duration) {
  const match = String(duration || '').trim().match(/^(\d+)\s*(D|W|M|Y)$/i)
  if (!match) return null
  const value = Number(match[1])
  const unit = match[2].toUpperCase()
  const days = { D: value, W: value * 7, M: value * 30, Y: value * 365 }[unit]
  return Math.floor((Date.now() - days * 86400000) / 1000)
}

function rawBarTimeToEpochSeconds(rawTime) {
  const ts = typeof rawTime === 'string' ? rawTime : String(rawTime ?? '')
  const intraday = ts.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
  if (intraday) {
    const [, y, mo, d, h, mi, s] = intraday
    const yy = Number(y), mm = Number(mo), dd = Number(d)
    const offset = etOffsetForDate(yy, mm, dd)
    return Math.floor(Date.UTC(yy, mm - 1, dd, Number(h) + offset, Number(mi), Number(s)) / 1000)
  }
  if (typeof rawTime === 'number' && Number.isFinite(rawTime)) return rawTime
  if (/^\d+$/.test(ts) && ts.length !== 8) return Number(ts)
  return rawTimeToEpoch(rawTime)
}

function formatEtIbkrTimestamp(epochSeconds) {
  const date = new Date(epochSeconds * 1000)
  if (isNaN(date.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date)
  const get = type => parts.find(part => part.type === type)?.value
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}${get('month')}${get('day')} ${hour}:${get('minute')}:${get('second')} US/Eastern`
}

function formatUtcDayKey(epochSeconds) {
  const date = new Date(epochSeconds * 1000)
  if (isNaN(date.getTime())) return null
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('')
}

export function canonicalBarToIbkrRawBar(bar, ibkrBarSize) {
  const epochSeconds = Number(bar?.barTime)
  const time = ibkrBarSize === '1 day' || ibkrBarSize === '1 week'
    ? formatUtcDayKey(epochSeconds)
    : formatEtIbkrTimestamp(epochSeconds)
  if (!time) return null
  return {
    time,
    open: Number(bar.open),
    high: Number(bar.high),
    low: Number(bar.low),
    close: Number(bar.close),
    volume: Number(bar.volume ?? 0),
  }
}

async function fetchCanonicalBarsInRange(symbol, barSize, from, to, authHeader, {
  allowEmpty = false,
  includeCurrent = false,
  session = MARKET_DATA_SESSION_REGULAR,
  minBars = null,
  limit = CANONICAL_BARS_FETCH_LIMIT,
} = {}) {
  const canonicalBarSize = canonicalBarSizeForIbkrSize(barSize)
  if (!canonicalBarSize || from === null) return null
  const marketSession = normalizeMarketDataSession(session)

  const params = new URLSearchParams({
    symbol,
    barSize: canonicalBarSize,
    provider: 'massive',
    priceAdjustment: 'raw',
    session: marketSession,
    from: String(from),
    to: String(to),
    limit: String(limit),
  })
  if (includeCurrent) params.set('includeCurrent', '1')
  if (Number.isInteger(minBars) && minBars > 0) params.set('minBars', String(minBars))
  const res = await fetch(`/api/marketData/bars?${params}`, {
    headers: { Authorization: authHeader },
  })
  if (!res.ok) throw new Error(`Canonical bars ${res.status}`)
  const data = await res.json()
  const diagnostics = data?.diagnostics || null
  const bars = (data.bars || [])
    .map(bar => canonicalBarToIbkrRawBar(bar, barSize))
    .filter(Boolean)
  if (!bars.length && !allowEmpty) throw new Error('No canonical bars')
  if (diagnostics?.fallback && !allowEmpty) {
    throw new Error(`Canonical bars fallback: ${diagnostics.fallbackReason || 'incomplete canonical window'}`)
  }
  if (diagnostics) {
    const level = diagnostics.fallback ? 'warn' : 'info'
    console[level]('[candleStore] canonical bars read', {
      symbol,
      barSize,
      from,
      to,
      count: bars.length,
      diagnostics,
    })
  }
  return dedupSort(bars)
}

async function fetchCanonicalBars(symbol, barSize, duration, authHeader, {
  session = MARKET_DATA_SESSION_REGULAR,
  includeCurrent = includeCurrentCanonicalBar(barSize, session),
} = {}) {
  const from = durationToFromSeconds(duration)
  const bars = await fetchCanonicalBarsInRange(
    symbol,
    barSize,
    from,
    Math.floor(Date.now() / 1000),
    authHeader,
    {
      includeCurrent,
      session,
      minBars: canonicalFirstPaintMinBars(barSize),
      limit: canonicalFirstPaintFetchLimit(barSize),
    },
  )
  if (bars && !canonicalBarsCanServe(bars, barSize, duration, session)) {
    throw new Error('Canonical bars do not cover requested duration')
  }
  return bars
}

export async function fetchCanonicalBarsForDuration(symbol, barSize, duration, options = {}) {
  if (!canonicalBarsReadEnabled()) return null
  const session = await getCachedSession()
  if (!session?.access_token) throw new Error('fetchBars: not authenticated')
  return fetchCanonicalBars(symbol, barSize, duration, `Bearer ${session.access_token}`, options)
}

export async function fetchCanonicalBarsBefore(symbol, barSize, beforeEpochSeconds, {
  chunkDays = CANONICAL_SCROLLBACK_CHUNK_DAYS,
  session: marketSession = MARKET_DATA_SESSION_REGULAR,
} = {}) {
  if (!canonicalBarsReadEnabled()) return []
  const session = await getCachedSession()
  if (!session?.access_token) return []
  const authHeader = `Bearer ${session.access_token}`
  const to = Math.max(0, Math.floor(Number(beforeEpochSeconds) || 0) - 1)
  if (!to) return []
  const from = Math.max(0, to - Math.max(1, chunkDays) * 86400)
  return fetchCanonicalBarsInRange(symbol, barSize, from, to, authHeader, {
    allowEmpty: true,
    session: marketSession,
  })
}

function durationCovers(bars, duration) {
  const m = duration.match(/^(\d+)\s*(D|W|M|Y)$/i)
  if (!m || !bars?.length) return true
  const num = parseInt(m[1])
  const unit = m[2].toUpperCase()
  const durationMs = { 'D': num * 86400000, 'W': num * 7 * 86400000, 'M': num * 30 * 86400000, 'Y': num * 365 * 86400000 }[unit] || 0
  const neededStartTime = (Date.now() - durationMs) / 1000
  const earliest = rawTimeToEpoch(bars[0]?.time)
  return !(earliest > 0 && earliest > neededStartTime + DURATION_COVERAGE_START_TOLERANCE_SECONDS)
}

function cacheCanServe(bars, barSize, duration) {
  return durationCovers(bars, duration) && barsReachLastSession(bars, barSize)
}

function hasMinimumCanonicalFirstPaintBars(bars, barSize) {
  const firstPaintBars = canonicalFirstPaintMinBars(barSize)
  return Boolean(firstPaintBars && bars.length >= firstPaintBars)
}

function canonicalBarsCanServe(bars, barSize, duration, session = MARKET_DATA_SESSION_REGULAR) {
  const marketSession = normalizeMarketDataSession(session)
  const reachesLatest = marketSession === MARKET_DATA_SESSION_ALL
    ? true
    : barsReachLastCompletedSession(bars, barSize)
  if (!reachesLatest) return false

  if (marketSession === MARKET_DATA_SESSION_ALL) {
    return durationCovers(bars, duration) || hasMinimumCanonicalFirstPaintBars(bars, barSize)
  }
  if (durationCovers(bars, duration)) return true

  return hasMinimumCanonicalFirstPaintBars(bars, barSize)
}

// EF-133: every path that returns bars or writes to _cache must dedup by
// time and strict-ascending sort. LWC v4 silently renders nothing on
// duplicate or unsorted timestamps; this is the store's contract to its
// consumers — they receive sanitized bars and never have to sort themselves.
function dedupSort(bars) {
  if (!bars?.length) return []
  const byTime = new Map()
  for (const b of bars) byTime.set(b.time, b)
  return [...byTime.values()].sort((a, b) => {
    const ax = typeof a.time === 'number' ? a.time : rawTimeToEpoch(a.time)
    const bx = typeof b.time === 'number' ? b.time : rawTimeToEpoch(b.time)
    return ax - bx
  })
}

// ── fetchBars: the ONE fetch path ───────────────────────────────────────────
// Path: browser Map (Tier 1) → Supabase candle_cache (Tier 2) → /api/ibkr/candles (Tier 4).
// Returns raw IBKR bars (timezone-independent); toCandles applies the tz
// conversion at render time.
//
// optContract: optional { underlying, expiry, strike, right }. When set, the
// Supabase tier is bypassed entirely — option contracts only live in Tier 1
// + Tier 4.

export async function fetchBars(symbol, barSize, duration, optContract, options = {}) {
  const marketSession = normalizeMarketDataSession(options.session)
  const parityMode = options.parityMode === true
  const useCanonicalBars = !optContract && canonicalBarsReadEnabled()
  const useStrictCanonicalBars = useCanonicalBars && canonicalBarsStrictModeEnabled()
  const key = `${useCanonicalBars ? 'CANONICAL:' : ''}${buildCacheKey(symbol, barSize, optContract, marketSession)}`

  // EF-181: acquire the Supabase JWT up front so the eventual /api/ibkr/candles
  // call carries Authorization. Throwing here is an intentional fast-fail
  // change in behavior — previously an unauthenticated session would have
  // failed server-side; now we never reach the cascade without a session.
  // The throw still flows through useCandleStore → status='error' → the
  // EF-133 1-shot 3 s auto-retry in TradeChart.
  const session = await getCachedSession()
  if (!session?.access_token) throw new Error('fetchBars: not authenticated')
  const authHeader = `Bearer ${session.access_token}`

  // Tier 1: Browser memory cache (5 min TTL)
  const mem = parityMode ? null : _cache.get(key)
  if (mem && Date.now() - mem.ts < CACHE_TTL) {
    const latestBarTime = mem.bars?.[mem.bars.length - 1]?.time ?? null
    if (
      isCacheFresh(new Date(mem.ts).toISOString(), barSize, latestBarTime)
      && (optContract ? durationCovers(mem.bars, duration) : cacheCanServe(mem.bars, barSize, duration))
    ) {
      return dedupSort(mem.bars)
    }
  }

  if (useCanonicalBars) {
    try {
      const canonicalBars = await fetchCanonicalBars(symbol, barSize, duration, authHeader, {
        session: marketSession,
        includeCurrent: parityMode ? false : includeCurrentCanonicalBar(barSize, marketSession),
      })
      if (canonicalBars) {
        if (!parityMode) _cache.set(key, { bars: canonicalBars, ts: Date.now() })
        return canonicalBars
      }
    } catch (err) {
      if (useStrictCanonicalBars || parityMode) {
        throw err
      }
      if (import.meta.env?.MODE === 'development') {
        console.debug('[candleStore] canonical bars fallback', { symbol, barSize, duration, err })
      }
    }
  }

  // Tier 2: Supabase persistent cache (skipped for option contracts)
  let cachedMeta = null
  let cached = null
  const useLegacyPersistentCache = !parityMode && !optContract && marketSession === MARKET_DATA_SESSION_REGULAR
  if (useLegacyPersistentCache) {
    try {
      cachedMeta = await getCachedCandleMeta(symbol, barSize)
      if (cachedMeta && isCacheFresh(cachedMeta.fetchedAt, barSize, cachedMeta.latestBarTime)) {
        cached = await getCachedCandles(symbol, barSize)
        if (cached && cacheCanServe(cached.bars, barSize, duration)) {
          const sorted = dedupSort(cached.bars)
          _cache.set(key, { bars: sorted, ts: Date.now() })
          return sorted
        }
      }
    } catch { /* Supabase offline — fall through */ }
  }

  // Tier 4: IBKR bridge (incremental delta when cache covers, full duration otherwise)
  let fetchDuration = duration
  let needsBackfill = false

  if (cached?.bars?.length && cached.latestBarTime) {
    if (!durationCovers(cached.bars, duration)) needsBackfill = true
    if (!needsBackfill) {
      const latestTime = rawTimeToEpoch(cached.latestBarTime)
      if (latestTime > 0) {
        const daysSinceLast = Math.ceil((Date.now() / 1000 - latestTime) / 86400)
        const deltaDays = daysSinceLast + 5
        if (deltaDays < 200) fetchDuration = `${deltaDays} D`
      }
    }
  }

  const params = new URLSearchParams({
    symbol: optContract?.underlying || symbol,
    barSize,
    duration: fetchDuration,
    whatToShow: 'TRADES',
    useRTH: marketSession === MARKET_DATA_SESSION_ALL ? '0' : '1',
  })
  if (optContract) {
    params.set('secType', 'OPT')
    params.set('expiry', optContract.expiry)
    params.set('strike', String(optContract.strike))
    params.set('right', optContract.right)
  }

  // EF-133: timeout on the IBKR bridge fetch. Keep this slightly above the
  // server proxy's 90s timeout so slow historical-bar calls can either return
  // bars or surface the server's structured 504 instead of being browser-
  // canceled first.
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), IBKR_CANDLE_FETCH_TIMEOUT_MS)
  let res
  try {
    res = await fetch(`/api/ibkr/candles?${params}`, {
      signal: controller.signal,
      headers: { Authorization: authHeader },
    })
  } catch (err) {
    if (useLegacyPersistentCache && (cached?.bars?.length || cachedMeta)) {
      const fallback = cached?.bars?.length
        ? cached
        : await Promise.resolve(getCachedCandles(symbol, barSize)).catch(() => null)
      if (fallback?.bars?.length && cacheCanServe(fallback.bars, barSize, duration)) {
        return dedupSort(fallback.bars)
      }
    }
    throw err
  } finally {
    clearTimeout(timeout)
  }
  if (!res.ok) {
    if (useLegacyPersistentCache && (cached?.bars?.length || cachedMeta)) {
      const fallback = cached?.bars?.length
        ? cached
        : await Promise.resolve(getCachedCandles(symbol, barSize)).catch(() => null)
      if (fallback?.bars?.length && cacheCanServe(fallback.bars, barSize, duration)) {
        return dedupSort(fallback.bars)
      }
    }
    throw new Error(`IBKR ${res.status}`)
  }
  const data = await res.json()
  const newBars = data.bars || []
  if (!newBars.length && !cached?.bars?.length) throw new Error('No data')

  let finalBars = newBars
  if (useLegacyPersistentCache && cached?.bars?.length) {
    finalBars = await mergeCachedCandles(symbol, barSize, newBars) || [...cached.bars, ...newBars]
  } else if (useLegacyPersistentCache) {
    setCachedCandles(symbol, barSize, newBars).catch(() => {})
    finalBars = newBars
  }

  finalBars = dedupSort(finalBars)

  // EF-133: don't poison the browser memory cache with empty results from
  // a transient IBKR failure. An empty `_cache` entry would block the next
  // 5 min of fetchBars calls (Tier 1 hit) from reaching Supabase or IBKR.
  if (finalBars.length) {
    _cache.set(key, { bars: finalBars, ts: Date.now() })
  }
  return finalBars
}

// ── toCandles: raw IBKR bars → chart-ready (timezone-converted) ─────────────
// CE-04g-UI-vwap-anchor: each converted candle now carries `rawEpochMs` —
// real-instant epoch ms derived from the raw IBKR timestamp BEFORE
// toChartTimestamp converts to LWC's fake-UTC convention. Intraday bars
// (`b.time` is epoch seconds or a numeric string of seconds) → ms = seconds
// * 1000. Daily/weekly bars (`b.time` is YYYYMMDD) → UTC-midnight ms of
// that date for shape consistency. The field is additive — existing
// consumers (LWC, strategy snapshot path) ignore it.

// ET-wall-clock offset for a given ET date — mirrors toChartTimestamp's
// step 1 (4 for EDT, 5 for EST). Kept local so this module stays
// self-contained (no timezone.js coupling).
const epochFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour: 'numeric', hour12: false,
})

function etOffsetForDate(y, mo, d) {
  const ref = new Date(Date.UTC(y, mo - 1, d, 16, 0, 0))
  const etHour = Number(
    epochFormatter.formatToParts(ref).find(p => p.type === 'hour')?.value
  )
  return 16 - etHour
}

function deriveRawEpochMs(rawTime) {
  const ts = typeof rawTime === 'string' ? rawTime : String(rawTime ?? '')

  // IBKR formatDate=1 intraday shape: "YYYYMMDD HH:mm:ss <zone>"
  // (the bridge passes `1` for formatDate to ib.reqHistoricalData). The
  // wall-clock is ET on US equity calendars, so we parse to a real UTC
  // instant the same way toChartTimestamp's intraday branch does.
  const intraday = ts.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
  if (intraday) {
    const [, y, mo, d, h, mi, s] = intraday
    const yy = Number(y), mm = Number(mo), dd = Number(d)
    const offset = etOffsetForDate(yy, mm, dd)
    return Date.UTC(yy, mm - 1, dd, Number(h) + offset, Number(mi), Number(s))
  }

  // Daily/weekly bars come in as the YYYYMMDD anchored date — either as
  // an 8-digit string OR an 8-digit number. Match before generic numeric
  // handling so a numeric YYYYMMDD isn't misread as epoch seconds.
  if (typeof rawTime === 'number' && Number.isFinite(rawTime)
      && rawTime >= 19000101 && rawTime <= 99991231 && Number.isInteger(rawTime)) {
    const y = Math.floor(rawTime / 10000)
    const mo = Math.floor((rawTime % 10000) / 100) - 1
    const d = rawTime % 100
    if (mo >= 0 && mo <= 11 && d >= 1 && d <= 31) {
      return Date.UTC(y, mo, d)
    }
  }
  if (/^\d{8}$/.test(ts) && Number(ts.slice(0, 4)) >= 1900 && Number(ts.slice(0, 4)) <= 9999) {
    const y = Number(ts.slice(0, 4))
    const mo = Number(ts.slice(4, 6)) - 1
    const d = Number(ts.slice(6, 8))
    return Date.UTC(y, mo, d)
  }

  // Numeric epoch seconds (defensive — IBKR `formatDate=1` returns strings,
  // but some test paths and the WS-tick path pass raw numbers).
  if (typeof rawTime === 'number' && Number.isFinite(rawTime)) {
    return rawTime * 1000
  }
  if (/^\d+$/.test(ts)) {
    return Number(ts) * 1000
  }
  return null
}

function readIbkrDayKey(rawTime) {
  if (typeof rawTime === 'string' && /^\d{8}$/.test(rawTime)) return rawTime
  if (
    typeof rawTime === 'number'
    && Number.isFinite(rawTime)
    && Number.isInteger(rawTime)
    && rawTime >= 19000101
    && rawTime <= 99991231
  ) {
    return String(rawTime)
  }
  return null
}

export function toCandles(rawBars, tz) {
  return (rawBars || []).map(b => {
    const ibkrDayKey = readIbkrDayKey(b.time)
    const candle = {
      time: toChartTimestamp(typeof b.time === 'number' ? String(b.time) : b.time, tz),
      rawEpochMs: deriveRawEpochMs(b.time),
      open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume,
    }
    return ibkrDayKey ? { ...candle, ibkrDayKey } : candle
  })
}

// ── aggregate65min: roll up 13 × 5-min bars into one 65-min bar ─────────────
// IBKR doesn't support a native 65-min bar size, so we fetch 5-min source
// data and roll it up here. Input must already be timezone-converted by
// toCandles(). 13 × 5-min = 1 × 65-min, anchored at 9:30 ET.

export function aggregate65min(candles) {
  if (!candles?.length) return []

  // Group by calendar day (the chart timestamp has the TZ offset baked in,
  // so getUTC* methods read the wall-clock day correctly).
  const days = new Map()
  for (const c of candles) {
    const d = new Date(c.time * 1000)
    const dayKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
    if (!days.has(dayKey)) days.set(dayKey, [])
    days.get(dayKey).push(c)
  }

  const result = []
  for (const [, dayBars] of days) {
    dayBars.sort((a, b) => a.time - b.time)
    // Chunk into groups of 13 (= 65 min / 5 min). Partial last chunk on
    // an early-close day or mid-bar live update still produces a valid OHLC.
    for (let i = 0; i < dayBars.length; i += 13) {
      const chunk = dayBars.slice(i, i + 13)
      if (chunk.length === 0) continue
      result.push({
        time: chunk[0].time,
        rawEpochMs: chunk[0].rawEpochMs,
        open: chunk[0].open,
        high: Math.max(...chunk.map(b => b.high)),
        low: Math.min(...chunk.map(b => b.low)),
        close: chunk[chunk.length - 1].close,
        volume: chunk.reduce(
          (sum, b) => sum + (Number.isFinite(b.volume) ? b.volume : 0),
          0
        ),
      })
    }
  }

  return result.sort((a, b) => a.time - b.time)
}

// ── IBKR_TF: timeframe → IBKR fetch params ──────────────────────────────────

export const IBKR_TF = {
  '1min':  { barSize: '1 min',   duration: '10 D' },
  '2min':  { barSize: '2 mins',  duration: '15 D' },
  '5min':  { barSize: '5 mins',  duration: '20 D' },
  '15min': { barSize: '15 mins', duration: '65 D' },
  '30min': { barSize: '30 mins', duration: '1 Y' },
  // IBKR has no native 65-min bar — we fetch 5-min source data
  // (120 calendar days) and aggregate to 65-min candles
  // via aggregate65min(). Shares the '5 mins' cache key with the 5min TF.
  '65min': { barSize: '5 mins',  duration: '120 D', aggregate: 65 },
  'daily': { barSize: '1 day',   duration: '3 Y' },
  'weekly': { barSize: '1 week', duration: '5 Y' },
}

// ── fetchFormingBar (was fetchBarsForCurrentCandle) ─────────────────────────
// EF-132: when the chart loads pre-market or on a holiday/weekend, barState
// is seeded from yesterday's last bar. The first RTH tick on a new day moves
// barTime forward; without a refetch, OHLC collapses to a single tick price
// and stays there for the rest of the bar's life. This helper fetches a
// short duration of fresh bars from IBKR (no cache) to recover the real
// OHLC for the forming bar.
//
// Returns { time, open, high, low, close } when a matching bar is found,
// or null otherwise (no match, fetch error, reentrancy hit). Pure — never
// mutates LWC refs. The hook (useCandleStore) is responsible for applying
// the result to its chart series.

export async function fetchFormingBar(symbol, timeframe, timezone, barTime, options = {}) {
  const marketSession = normalizeMarketDataSession(options.session)
  const refetchCooldownMs = Number.isFinite(options.refetchCooldownMs)
    ? Math.max(0, Number(options.refetchCooldownMs))
    : 60000
  const refetchKey = `${symbol}:${timeframe}:${barTime}`
  if (_refetchingBar === refetchKey) return null
  _refetchingBar = refetchKey

  try {
    const tf = IBKR_TF[timeframe] || IBKR_TF['daily']
    const shortDuration = (timeframe === 'daily' || timeframe === 'weekly') ? '2 D' : '1 D'

    const isOpt = isOCCSymbol(symbol)
    const occ = isOpt ? parseOCC(symbol) : null
    const fetchSymbol = occ ? occ.underlying : symbol

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return null

    if (!occ && canonicalBarsReadEnabled()) {
      const canonicalBarSize = canonicalBarSizeForTimeframe(timeframe)
      const currentSession = getMarketSession()
      const activeForSession = marketSession === MARKET_DATA_SESSION_REGULAR
        ? currentSession === 'regular'
        : ['pre', 'regular', 'post'].includes(currentSession)
      if (!canonicalBarSize || !activeForSession) return null

      const intervalSeconds = {
        '1min': 60,
        '2min': 2 * 60,
        '5min': 5 * 60,
        '15min': 15 * 60,
        '30min': 30 * 60,
        '65min': 65 * 60,
      }[canonicalBarSize]
      if (!intervalSeconds) return null

      const to = Math.floor(Date.now() / 1000)
      const from = Math.max(0, to - Math.max(86400, intervalSeconds * 4))
      const bars = await fetchCanonicalBarsInRange(
        fetchSymbol,
        canonicalFetchBarSizeForTimeframe(timeframe),
        from,
        to,
        `Bearer ${session.access_token}`,
        { allowEmpty: true, includeCurrent: true, session: marketSession },
      )
      const todayBar = (bars || [])
        .map((bar) => toCandles([bar], timezone)[0])
        .find((c) => c?.time === barTime)
      if (!todayBar) return null

      return {
        time: barTime,
        rawEpochMs: todayBar.rawEpochMs,
        open: todayBar.open,
        high: todayBar.high,
        low: todayBar.low,
        close: todayBar.close,
        volume: todayBar.volume,
      }
    }

    const params = new URLSearchParams({
      symbol: fetchSymbol,
      barSize: tf.barSize,
      duration: shortDuration,
      whatToShow: 'TRADES',
      useRTH: '1',
    })
    if (occ) {
      params.set('secType', 'OPT')
      params.set('expiry', occ.expiry)
      params.set('strike', String(occ.strike))
      params.set('right', occ.right)
    }

    // Direct fetch — intentionally bypasses both _cache (browser memory)
    // and the Supabase cache so we always get IBKR's freshest view of the
    // forming bar. EF-181: attach Bearer; on no session, return null
    // (non-fatal — live ticks remain the fallback per existing contract).
    const res = await fetch(`/api/ibkr/candles?${params}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (!res.ok) return null
    const data = await res.json()
    const bars = data.bars || []
    if (!bars.length) return null

    const converted = toCandles(bars, timezone)
    const todayBar = converted.find(c => c.time === barTime)
    if (!todayBar) return null

    if (!occ && ['5 mins', '15 mins', '30 mins'].includes(tf.barSize)) {
      const completedBars = bars.filter((_, idx) => converted[idx]?.time !== barTime)
      if (completedBars.length) {
        mergeCachedCandles(fetchSymbol, tf.barSize, completedBars).catch(() => {})
      }
    }

    return {
      time: barTime,
      rawEpochMs: todayBar.rawEpochMs,
      open: todayBar.open,
      high: todayBar.high,
      low: todayBar.low,
      close: todayBar.close,
      // CE-04g-UI-indicators-live-tick: forward volume so the live-tick
      // pump's `synthesizeFormingBar` can read it from `liveCandlesRef`
      // after `applyFormingBarCorrection` merges this correction in.
      volume: todayBar.volume,
    }
  } catch {
    // Non-fatal — caller falls back to live ticks.
    return null
  } finally {
    if (refetchCooldownMs === 0) {
      if (_refetchingBar === refetchKey) _refetchingBar = null
    } else {
      // Allow a re-attempt later in case the first fetch raced the
      // bridge or IBKR hadn't emitted today's bar yet.
      setTimeout(() => { if (_refetchingBar === refetchKey) _refetchingBar = null }, refetchCooldownMs)
    }
  }
}

// ── test-only ───────────────────────────────────────────────────────────────
// Resets module-level state between Vitest cases. Not for production use.
export function clearMemoryCache() {
  _cache.clear()
  _refetchingBar = null
}
